/** @format */

/**
 * Sanctioning-body utilities — kept in one module so route validation,
 * nav ordering, and runtime narrowing share a single source of truth.
 *
 * The set of recognised slugs mirrors backend `SanctioningBody.choices`
 * (`server/backend/models/nascar.py`). Adding a new body requires:
 *   1) backend `SanctioningBody` enum + a backfill migration
 *   2) `SanctioningBodySlug` union below
 *   3) `SANCTIONING_BODY_ORDER` to place it in the nav
 *   4) PostHog flag `accessible-sanctioning-bodies` payload schema update
 */

import { z } from "zod";
import type { SanctioningBodySlug } from "@/api/configApi";

export type { SanctioningBodySlug };

/** Source-of-truth Zod enum for route param validation. */
export const SanctioningBodySchema = z.enum(["nascar", "arca", "grcup"]);

/**
 * Canonical display order for the top-bar nav. NASCAR first because it is
 * the primary use-case and the home (`/`) lands there.
 */
export const SANCTIONING_BODY_ORDER: ReadonlyArray<SanctioningBodySlug> = [
  "nascar",
  "arca",
  "grcup",
];

/**
 * Path segment for a sanctioning body. NASCAR maps to root `/` because
 * that's the home; the others are top-level slugs handled by the
 * parameterised route.
 */
export function pathForSanctioningBody(body: SanctioningBodySlug): string {
  return body === "nascar" ? "/" : `/${body}`;
}

/** Narrow an arbitrary string into a slug, or `null` if it isn't one. */
export function asSanctioningBody(value: unknown): SanctioningBodySlug | null {
  const result = SanctioningBodySchema.safeParse(value);
  return result.success ? result.data : null;
}

/**
 * Sort an arbitrary list of slugs into canonical order. Slugs not in
 * `SANCTIONING_BODY_ORDER` are dropped (defensive — they shouldn't appear).
 */
export function sortByCanonicalOrder(
  bodies: ReadonlyArray<SanctioningBodySlug>
): SanctioningBodySlug[] {
  const set = new Set(bodies);
  return SANCTIONING_BODY_ORDER.filter(b => set.has(b));
}
